import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { PlatformLanguage, PlatformUnits } from "@/generated-api";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import PlatformLanguageSelector from "./data-form/PlatformLanguageSelector";
import { useAcceptTermsSetLanguage } from "@/tanstack-hooks";
import { useToast } from "@/hooks/use-toast";
import { TRANSLATIONS } from "@/translations";
import BadgeSelector from "./data-form/BadgeSelector";
import SpinnerSmall from "../SpinnerSmall";

const getDefaultLanguage = () => {
  // Get user's browser language
  const userLanguage = navigator.language.toLowerCase();
  // Check if the language starts with 'ru' for Russian
  return userLanguage.startsWith("ru")
    ? PlatformLanguage.RUSSIAN
    : PlatformLanguage.ENGLISH;
};

const formSchema = z.object({
  acceptTerms: z.boolean().refine((val) => val === true, {
    message:
      TRANSLATIONS.ENGLISH.ONBOARDING_TERMS.ERRORS.TERMS_CONFIRMATION_REQUIRED,
  }),
  ageConfirmation: z.boolean().refine((val) => val === true, {
    message:
      TRANSLATIONS.ENGLISH.ONBOARDING_TERMS.ERRORS.AGE_CONFIRMATION_REQUIRED,
  }),
  language: z.nativeEnum(PlatformLanguage),
  units: z
    .array(z.nativeEnum(PlatformUnits))
    .min(1, "One unit at least")
    .max(1, "One unit at most"),
});

type FormSchema = z.infer<typeof formSchema>;

export default function TermsAndLanguageForm() {
  const { toast } = useToast();
  const acceptTermsSetLanguageMutation = useAcceptTermsSetLanguage();

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      acceptTerms: false,
      ageConfirmation: false,
      language: getDefaultLanguage(),
      units: [PlatformUnits.METRIC],
    },
  });

  const t = TRANSLATIONS[form.watch("language")];

  function handleAcceptTerms(values: FormSchema) {
    acceptTermsSetLanguageMutation.mutate(
      {
        preferred_language: values.language,
        preferred_units: values.units[0],
        referred_by: localStorage.getItem("referralCode"),
      },
      {
        onError: () => {
          toast({
            variant: "destructive",
            title: t.ONBOARDING_TERMS.ERRORS.ACCEPT_TERMS,
            description: t.SHARED.ERRORS.PERSISTENT,
            className: "dark",
          });
        },
      }
    );
  }

  return (
    <Card className="dark w-full max-w-sm mx-auto rounded-3xl border border-zinc-600">
      <CardHeader>
        <CardTitle>{t.ONBOARDING_TERMS.LABELS.TERMS_TITLE}</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleAcceptTerms)}
            className="flex flex-col gap-y-4"
          >
            <FormField
              control={form.control}
              name="acceptTerms"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 hover:cursor-pointer">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="mt-2 h-6 w-6 rounded-md"
                    />
                  </FormControl>
                  <FormLabel className="hover:cursor-pointer">
                    {t.ONBOARDING_TERMS.LABELS.TERMS_CONFIRMATION}
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ageConfirmation"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 hover:cursor-pointer">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="mt-2 h-6 w-6 rounded-md"
                    />
                  </FormControl>
                  <FormLabel className="hover:cursor-pointer">
                    {t.ONBOARDING_TERMS.LABELS.AGE_CONFIRMATION}
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2 mt-4">
              <FormLabel className="text-lg font-semibold">
                {t.ONBOARDING_TERMS.LABELS.LANGUAGE_PREFERENCE}
              </FormLabel>
              <FormField
                control={form.control}
                name="language"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <PlatformLanguageSelector formField={field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-2 mt-2">
              <FormLabel className="text-lg font-semibold flex flex-row items-center">
                {t.ONBOARDING_TERMS.LABELS.UNITS_PREFERENCE}
              </FormLabel>
              <FormField
                control={form.control}
                name="units"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <BadgeSelector
                        selectedValues={field.value || []}
                        onChange={field.onChange}
                        enumObject={PlatformUnits}
                        translation={t}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <Button
              type="submit"
              className="w-full mt-2"
              disabled={
                !form.formState.isValid ||
                form.formState.isSubmitting ||
                acceptTermsSetLanguageMutation.isPending
              }
            >
              {acceptTermsSetLanguageMutation.isPending ? (
                <SpinnerSmall black={true} />
              ) : (
                t.SHARED.LABELS.CONTINUE_BUTTON
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
